import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, FileSearch, Globe, Search, Calculator,
  Users, ClipboardList, FileText, Presentation,
  MessageSquare, Box, Target, Shield, Lock, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const features = [
  {
    icon: FileSearch,
    title: 'CIM Extraction',
    description: 'AI extracts financials from CIM PDFs and infers formulas from static data.',
  },
  {
    icon: Globe,
    title: 'Sector Research',
    description: 'Deep sector analysis with market sizing, competitive landscape, and trend analysis.',
  },
  {
    icon: Search,
    title: 'Transaction Search',
    description: 'Neural M&A transaction discovery with semantic and similar-company search.',
  },
  {
    icon: Calculator,
    title: 'AI Financial Model',
    description: 'AI-powered Excel model generation with PE-standard formatting and sensitivity analysis.',
  },
  {
    icon: Users,
    title: 'IC Committee (Simulated)',
    description: '8-persona AI analysis with voting simulation and chairman synthesis.',
  },
  {
    icon: ClipboardList,
    title: 'IC DD Questions',
    description: 'AI-generated due diligence questionnaires with citation backing.',
  },
  {
    icon: FileText,
    title: 'IC Memo',
    description: 'Section-by-section IC memo generation aligned with committee findings.',
  },
  {
    icon: Presentation,
    title: 'Presentations',
    description: 'AI-generated IC and chat-based presentations from 7 data sources with PPTX export.',
  },
  {
    icon: MessageSquare,
    title: 'AI Chat',
    description: 'Conversational AI with RAG, citations, web search, and streaming responses.',
  },
  {
    icon: Box,
    title: 'Customer Cube',
    description: 'Multi-dimensional customer analysis across segments, cohorts, and concentration risk.',
  },
  {
    icon: Target,
    title: 'Competitor Intelligence',
    description: 'Competitive positioning with Porter\'s Five Forces and strategic group mapping.',
  },
];

const badges = [
  { icon: Shield, label: 'SOC 2 Ready' },
  { icon: Lock, label: 'End-to-End Encryption' },
  { icon: Users, label: 'Role-Based Access' },
  { icon: Zap, label: '99.9% Uptime' },
];

export default function CorbelLandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-5 sm:px-10">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-lg">C</span>
          </div>
          <div>
            <h1 className="text-white font-semibold text-sm leading-tight">Corbel Capital</h1>
            <p className="text-blue-300/70 text-xs">AI Platform</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="text-blue-200 hover:text-white hover:bg-white/10"
            onClick={() => navigate('/login')}
          >
            Log In
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/50"
            onClick={() => navigate('/signup')}
          >
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto text-center px-6 pt-20 pb-16 sm:pt-28 sm:pb-24">
        <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-sm text-blue-300 mb-8">
          <Zap className="h-3.5 w-3.5" />
          AI-Powered Due Diligence
        </div>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
          Corbel Capital
          <br />
          <span className="bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
            AI Platform
          </span>
        </h2>
        <p className="mt-6 text-lg text-blue-200/80 max-w-2xl mx-auto leading-relaxed">
          End-to-end AI deal intelligence from CIM upload to IC memo.
          Extract financials, run committee simulations, generate presentations,
          and monitor portfolio companies &mdash; all in one platform.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            size="lg"
            className="bg-blue-600 hover:bg-blue-500 text-white shadow-xl shadow-blue-900/50 h-12 px-8 text-base"
            onClick={() => navigate('/login')}
          >
            Log In to Platform
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="border-blue-400/50 text-blue-400 hover:bg-white/5 hover:text-white h-12 px-8 text-base"
            onClick={() => navigate('/signup')}
          >
            Create Account
          </Button>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <h3 className="text-center text-sm font-semibold uppercase tracking-wider text-blue-400 mb-10">
          Platform Capabilities
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f) => (
            <Card
              key={f.title}
              className="bg-white/5 border-white/10 hover:bg-white/[0.08] transition-colors"
            >
              <CardContent className="p-6">
                <f.icon className="h-8 w-8 text-blue-400 mb-4" />
                <h4 className="text-white font-semibold mb-2">{f.title}</h4>
                <p className="text-blue-200/60 text-sm leading-relaxed">{f.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Security Badges */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
          {badges.map((b) => (
            <div key={b.label} className="flex items-center gap-2 text-blue-300/60 text-sm">
              <b.icon className="h-4 w-4" />
              {b.label}
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 text-center text-sm text-blue-300/40">
        Corbel Capital Partners
      </footer>
    </div>
  );
}
